'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronRight, MousePointer2, RotateCcw } from 'lucide-react';
import { Money } from '@/components/ui/money';
import { cn } from '@/lib/utils';
import s from './landing.module.css';

/** Sample October books. Net income is sales less every expense line, and
    selling fees are the sum of the three channels. */
const JOURNAL = [
  { account: 'Cash · Chase ••4821', debit: 1231.84 },
  { account: 'Final value fees', debit: 178.46 },
  { account: 'Promoted listings', debit: 22.8 },
  { account: 'Shipping labels', debit: 41.3 },
  { account: 'Sales returns', debit: 38 },
  { account: 'Sales', credit: 1512.4 },
];
const TOTAL = 1512.4;

function Expand({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div className={cn(s.expand, open && s.expandOpen)} inert={!open}>
      <div className="min-h-0">{children}</div>
    </div>
  );
}

function Line({
  label,
  amount,
  depth = 0,
  strong = false,
  open,
  onToggle,
  buttonRef,
}: {
  label: string;
  amount: number;
  depth?: number;
  strong?: boolean;
  open?: boolean;
  onToggle?: () => void;
  buttonRef?: (element: HTMLButtonElement | null) => void;
}) {
  const content = (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        {onToggle ? (
          <ChevronRight
            className={cn(
              'size-3.5 shrink-0 text-gray-400 transition-transform duration-300',
              open && 'rotate-90 text-gray-700'
            )}
            aria-hidden="true"
          />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <span className={cn('truncate', strong && 'font-semibold')}>
          {label}
        </span>
      </span>
      <Money
        amount={amount}
        accounting
        className={cn('shrink-0 tabular-nums', strong && 'font-semibold')}
      />
    </>
  );
  const className = cn(
    'flex w-full items-center justify-between gap-4 rounded-lg py-2 pr-3 text-left text-[13px] text-gray-800',
    depth === 0 ? 'pl-2' : depth === 1 ? 'pl-7' : 'pl-12'
  );
  if (!onToggle) return <div className={className}>{content}</div>;
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className={cn(
        className,
        'focus-visible:ring-accent-50 outline-hidden transition-colors hover:bg-gray-50 focus-visible:ring-2',
        open && 'bg-accent-5 hover:bg-accent-5'
      )}
    >
      {content}
    </button>
  );
}

export function LedgerDrilldown() {
  const cardRef = useRef<HTMLDivElement>(null);
  const feesRef = useRef<HTMLButtonElement | null>(null);
  const payoutRef = useRef<HTMLButtonElement | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // The server renders every level open so the content reads without JavaScript.
  const [fees, setFees] = useState(true);
  const [payout, setPayout] = useState(true);
  const [cursor, setCursor] = useState({
    x: 0,
    y: 0,
    shown: false,
    press: false,
  });

  const stopTour = () => {
    for (const timer of timers.current) clearTimeout(timer);
    timers.current = [];
    setCursor(current => ({ ...current, shown: false }));
  };

  const pointAt = (button: HTMLButtonElement | null, press = false) => {
    const card = cardRef.current;
    if (!button || !card) return;
    const box = card.getBoundingClientRect();
    const target = button.getBoundingClientRect();
    setCursor({
      x: target.left - box.left + Math.min(170, target.width * 0.45),
      y: target.top - box.top + target.height / 2,
      shown: true,
      press,
    });
  };

  const playTour = () => {
    stopTour();
    setFees(false);
    setPayout(false);
    const at = (ms: number, step: () => void) =>
      timers.current.push(setTimeout(step, ms));
    at(500, () => pointAt(feesRef.current));
    at(1300, () => pointAt(feesRef.current, true));
    at(1450, () => {
      setFees(true);
      setCursor(current => ({ ...current, press: false }));
    });
    at(2300, () => pointAt(payoutRef.current));
    at(3100, () => pointAt(payoutRef.current, true));
    at(3250, () => {
      setPayout(true);
      setCursor(current => ({ ...current, press: false }));
    });
    at(4400, () => setCursor(current => ({ ...current, shown: false })));
  };

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (card.getBoundingClientRect().top < window.innerHeight) return;
    setFees(false);
    setPayout(false);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        playTour();
      },
      { threshold: 0.6 }
    );
    observer.observe(card);
    const pending = timers.current;
    return () => {
      observer.disconnect();
      for (const timer of pending) clearTimeout(timer);
    };
    // playTour only reads refs and stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={cardRef}
      className="relative rounded-3xl bg-white p-3 text-gray-900 shadow-[0_40px_100px_-40px_rgb(0_0_0/0.9)] ring-1 ring-white/10 sm:p-4"
      onPointerDown={stopTour}
    >
      <div className="flex items-center justify-between gap-3 px-2 pt-1 pb-3">
        <div>
          <p className="text-sm font-semibold">Income statement</p>
          <p className="text-xs text-gray-500">October, month to date</p>
        </div>
        <button
          type="button"
          onClick={playTour}
          className="focus-visible:ring-accent-50 inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs text-gray-600 outline-hidden hover:bg-gray-50 focus-visible:ring-2"
        >
          <RotateCcw className="size-3" aria-hidden="true" />
          Replay
        </button>
      </div>

      <div className="border-t border-gray-100 pt-1">
        <Line label="Sales" amount={21906.5} />
        <Line
          label="Selling fees"
          amount={-2481.06}
          open={fees}
          onToggle={() => setFees(value => !value)}
          buttonRef={element => {
            feesRef.current = element;
          }}
        />
        <Expand open={fees}>
          <Line
            label="eBay payouts (6)"
            amount={-1804.22}
            depth={1}
            open={payout}
            onToggle={() => setPayout(value => !value)}
            buttonRef={element => {
              payoutRef.current = element;
            }}
          />
          <Expand open={payout}>
            <div className="my-1 mr-1 ml-12 rounded-xl border border-gray-200 bg-gray-50/70 p-3">
              <p className="text-[11px] font-medium text-gray-500">
                Journal entry · eBay payout, October 14
              </p>
              <table className="mt-2 w-full text-[12px]">
                <thead>
                  <tr className="text-[10px] text-gray-400 uppercase">
                    <th className="pb-1 text-left font-medium">Account</th>
                    <th className="pb-1 text-right font-medium">Debit</th>
                    <th className="pb-1 text-right font-medium">Credit</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {JOURNAL.map(line => (
                    <tr key={line.account} className="text-gray-700">
                      <td className="py-0.5 pr-2">{line.account}</td>
                      <td className="py-0.5 text-right">
                        {line.debit ? <Money amount={line.debit} /> : ''}
                      </td>
                      <td className="py-0.5 pl-2 text-right">
                        {line.credit ? <Money amount={line.credit} /> : ''}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-gray-200 font-semibold text-gray-900">
                    <td className="pt-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-px text-[10px] font-medium text-emerald-700">
                        <Check className="size-3" aria-hidden="true" />
                        Balanced
                      </span>
                    </td>
                    <td className="pt-1.5 text-right">
                      <Money amount={TOTAL} />
                    </td>
                    <td className="pt-1.5 pl-2 text-right">
                      <Money amount={TOTAL} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Expand>
          <Line label="Amazon settlements (2)" amount={-512.3} depth={1} />
          <Line label="Shopify payouts (4)" amount={-164.54} depth={1} />
        </Expand>
        <Line label="Cost of goods sold" amount={-6930.4} />
        <Line label="Operating expenses" amount={-4082.68} />
        <div className="mt-1 border-t border-gray-100 pt-1">
          <Line label="Net income" amount={8412.36} strong />
        </div>
      </div>

      <span
        aria-hidden="true"
        className={cn(s.tourCursor, cursor.shown && s.tourCursorShown)}
        style={{ translate: `${cursor.x}px ${cursor.y}px` }}
      >
        <MousePointer2
          className={cn(
            'fill-umbra size-5 text-white transition-transform duration-150',
            cursor.press && 'scale-85'
          )}
        />
        {cursor.press && <span className={s.tourRipple} />}
      </span>
    </div>
  );
}
